import { useState, useRef, useEffect, useCallback } from 'react'
import { useIntl } from 'react-intl'
import { styled, CircularProgress } from '@mui/material'
import { DiscussionThread } from './DiscussionThread'
import { NewThreadForm } from './NewThreadForm'
import { Button } from '../../../../components/buttons'
import { useDiscussion } from './hooks/useDiscussion'
import { CloseButton, StyledDivider } from '../commons'

interface DiscussionPanelProps {
  versionPageUuid: string
  projectUuid: string
  isOpen: boolean
  onClose: () => void
}

const useAutoScrollToBottom = (shouldTriggerScroll: boolean) => {
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!shouldTriggerScroll || !containerRef.current) return

    const container = containerRef.current
    const scrollToBottom = () => {
      container.scrollTo({
        top: container.scrollHeight,
        behavior: 'smooth',
      })
    }

    const observer = new MutationObserver(() => {
      requestAnimationFrame(() => requestAnimationFrame(scrollToBottom))
    })

    observer.observe(container, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style', 'class'],
    })

    requestAnimationFrame(() => requestAnimationFrame(scrollToBottom))

    return () => observer.disconnect()
  }, [shouldTriggerScroll])

  return containerRef
}

export const DiscussionPanel = ({
  versionPageUuid,
  projectUuid,
  isOpen,
  onClose,
}: DiscussionPanelProps) => {
  const [showNewThreadForm, setShowNewThreadForm] = useState(false)
  const [shouldScrollToNewPost, setShouldScrollToNewPost] = useState(false)
  const intl = useIntl()

  const { discussion, isLoading, refreshDiscussion } = useDiscussion({
    versionPageUuid,
  })

  const panelContentRef = useAutoScrollToBottom(
    showNewThreadForm || shouldScrollToNewPost
  )

  useEffect(() => {
    if (shouldScrollToNewPost && !isLoading) {
      const resetScrollFlag = setTimeout(() => {
        setShouldScrollToNewPost(false)
      }, 500)
      return () => clearTimeout(resetScrollFlag)
    }
  }, [shouldScrollToNewPost, isLoading])

  const handleRefreshAfterPost = () => {
    setShouldScrollToNewPost(true)
    refreshDiscussion()
  }

  const renderThreadContent = useCallback(() => {
    if (isLoading) return null
    if (!discussion?.discussionThreads?.length) {
      return showNewThreadForm ? null : (
        <EmptyMessage>
          {intl.formatMessage({ id: 'discussion.empty' })}
        </EmptyMessage>
      )
    }

    return (
      <ThreadListContainer>
        {discussion.discussionThreads
          .sort((a, b) => Number(a.createdAt) - Number(b.createdAt))
          .map(thread => (
            <DiscussionThread
              key={thread.threadUuid}
              thread={thread}
              projectUuid={projectUuid}
              versionPageUuid={versionPageUuid}
              onRefresh={refreshDiscussion}
            />
          ))}
      </ThreadListContainer>
    )
  }, [
    isLoading,
    discussion,
    showNewThreadForm,
    intl,
    projectUuid,
    versionPageUuid,
    refreshDiscussion,
  ])

  if (!versionPageUuid || !isOpen) return null

  return (
    <PanelContainer>
      <PanelHeader>
        <HeaderLeft>
          <Title>{intl.formatMessage({ id: 'discussion' })}</Title>
          {versionPageUuid && (
            <Button
              onClick={() => setShowNewThreadForm(true)}
              variant="outlined"
              size="s"
            >
              {intl.formatMessage({ id: 'discussion.new' })}
            </Button>
          )}
        </HeaderLeft>
        <CloseButton onClick={onClose} />
      </PanelHeader>
      <StyledDivider />

      <PanelContent ref={panelContentRef}>
        {isLoading && (
          <LoadingContainer>
            <CircularProgress />
          </LoadingContainer>
        )}

        {renderThreadContent()}

        {!isLoading && showNewThreadForm && (
          <NewThreadForm
            projectUuid={projectUuid}
            versionPageUuid={versionPageUuid}
            discussionUuid={discussion?.discussionUuid}
            onClose={() => setShowNewThreadForm(false)}
            onRefresh={handleRefreshAfterPost}
          />
        )}
      </PanelContent>
    </PanelContainer>
  )
}

const PanelContainer = styled('div')({
  width: '400px',
  height: '100%',
  backgroundColor: '#ffffff',
  border: '1px solid #e0e0e0',
  display: 'flex',
  flexDirection: 'column',
  flexShrink: 0,
})

const PanelHeader = styled('div')({
  padding: '16px',
  height: '56px',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
})

const HeaderLeft = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
})

const Title = styled('h3')({
  margin: 0,
  fontSize: '14px',
  fontWeight: 600,
})

const PanelContent = styled('div')({
  flex: 1,
  padding: '16px',
  overflowY: 'auto',
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
})

const ThreadListContainer = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
})

const EmptyMessage = styled('div')({
  textAlign: 'center',
  color: '#666',
  fontSize: '14px',
  padding: '32px 16px',
})

const LoadingContainer = styled('div')({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  padding: '32px 16px',
})
